export type AcceptFormat =
  | 'png'
  | 'jpeg'
  | 'tiff'
  | 'bmp'
  | 'pdf'
  | 'doc'
  | 'xls'
  | 'csv'
  | 'odt'
  | 'html'
  | 'ppt'
  | 'txt'
  | 'mp4'
  | 'webp'
  | 'gif'
  | 'msg';

export type SignatureValidatedFormat =
  | 'png'
  | 'jpeg'
  | 'tiff'
  | 'bmp'
  | 'pdf'
  | 'mp4'
  | 'webp'
  | 'gif';

export type DetectedFileFormat = SignatureValidatedFormat | 'heic' | 'heif' | 'avif';

export const FILE_SIGNATURE_MISMATCH_ERROR =
  'File content does not match the selected file type. Please upload a valid file.';
export const HEIC_UNSUPPORTED_ERROR =
  'HEIC/HEIF images are not supported. Please convert the file to PNG or JPEG and upload it again.';

const MIME_SIGNATURE_FORMATS: Record<string, SignatureValidatedFormat[]> = {
  'image/png': ['png'],
  'image/jpeg': ['jpeg'],
  'image/tiff': ['tiff'],
  'image/bmp': ['bmp'],
  'application/pdf': ['pdf'],
  'video/mp4': ['mp4'],
  'image/webp': ['webp'],
  'image/gif': ['gif'],
};

const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx'];
const HEIF_BRANDS = ['heif', 'mif1', 'msf1'];
const AVIF_BRANDS = ['avif', 'avis'];
const MP4_BRANDS = ['isom', 'iso2', 'mp41', 'mp42', 'avc1', 'M4V ', 'M4A '];

const bytesMatch = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((byte, index) => bytes[offset + index] === byte);

const readAscii = (bytes: Uint8Array, offset: number, length: number) =>
  Array.from(bytes.slice(offset, offset + length))
    .map(byte => String.fromCharCode(byte))
    .join('');

export const detectFileFormat = async (file: File): Promise<DetectedFileFormat | undefined> => {
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());

  if (bytesMatch(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (bytesMatch(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (bytesMatch(bytes, [0x25, 0x50, 0x44, 0x46])) return 'pdf';
  if (readAscii(bytes, 0, 6) === 'GIF87a' || readAscii(bytes, 0, 6) === 'GIF89a') return 'gif';
  if (readAscii(bytes, 0, 2) === 'BM') return 'bmp';
  if (readAscii(bytes, 0, 4) === 'II*\x00' || readAscii(bytes, 0, 4) === 'MM\x00*')
    return 'tiff';
  if (readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'WEBP') return 'webp';

  if (readAscii(bytes, 4, 4) === 'ftyp') {
    const brand = readAscii(bytes, 8, 4);

    if (HEIC_BRANDS.includes(brand)) return 'heic';
    if (HEIF_BRANDS.includes(brand)) return 'heif';
    if (AVIF_BRANDS.includes(brand)) return 'avif';
    if (MP4_BRANDS.includes(brand)) return 'mp4';
  }

  return undefined;
};

export const getFileContentError = async (
  file: File,
  acceptFormat: Array<AcceptFormat>,
  customUnsupportedFormatError?: string,
) => {
  const declaredFormats = MIME_SIGNATURE_FORMATS[file.type] || [];
  const detectedFormat = await detectFileFormat(file);

  if (detectedFormat === 'heic' || detectedFormat === 'heif') {
    return HEIC_UNSUPPORTED_ERROR;
  }
  if (detectedFormat === 'avif') {
    return customUnsupportedFormatError || 'This image format is not supported';
  }

  if (declaredFormats.length > 0) {
    if (!detectedFormat) return FILE_SIGNATURE_MISMATCH_ERROR;
    if (!declaredFormats.includes(detectedFormat)) return FILE_SIGNATURE_MISMATCH_ERROR;
  }

  if (detectedFormat && !acceptFormat.includes(detectedFormat)) {
    return customUnsupportedFormatError || `File type ${file.type} is not support`;
  }

  return '';
};
